import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Logo } from "../components/Logo";
import { Loader2 } from "lucide-react";

const CONCRETE_BG =
  "https://images.pexels.com/photos/13777953/pexels-photo-13777953.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative bg-[#0A0A0A] p-4">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{ backgroundImage: `url(${CONCRETE_BG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/40" />

      <div className="relative w-full max-w-md bg-[#16181A]/80 backdrop-blur-xl border border-[#2E3033] p-8 shadow-[0_4px_40px_rgba(0,0,0,0.6)]">
        <div className="flex justify-center mb-6">
          <Logo className="h-16" />
        </div>
        <h1 className="font-heading text-2xl font-black text-white text-center tracking-tight">
          YARD ORGANIZER
        </h1>
        <p className="text-center text-[#6E7787] text-xs font-mono uppercase tracking-[0.2em] mt-1 mb-8">
          Production Control Login
        </p>

        {error && (
          <div
            data-testid="login-error"
            className="mb-4 border border-[#EF4444]/50 bg-[#EF4444]/10 text-[#EF4444] text-sm px-4 py-2 font-mono"
          >
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#6E7787]">
              Email
            </label>
            <input
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-1 bg-[#0A0A0A] border border-[#2E3033] px-4 py-2.5 text-white placeholder-[#6E7787] focus:border-[#2560C9] focus:outline-none focus:ring-1 focus:ring-[#2560C9] transition-all font-mono text-sm"
              placeholder="operator@eazikast.com"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#6E7787]">
              Password
            </label>
            <input
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full mt-1 bg-[#0A0A0A] border border-[#2E3033] px-4 py-2.5 text-white placeholder-[#6E7787] focus:border-[#2560C9] focus:outline-none focus:ring-1 focus:ring-[#2560C9] transition-all font-mono text-sm"
              placeholder="••••••••"
            />
          </div>
          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-[#2560C9] hover:bg-[#3B75DF] text-white font-medium py-2.5 flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            SIGN IN
          </button>
        </form>

        <p className="text-center text-[#6E7787] text-sm mt-6">
          No account?{" "}
          <Link data-testid="go-to-register" to="/register" className="text-[#3B75DF] hover:underline">
            Register
          </Link>
        </p>
        <p data-testid="login-legal" className="text-center text-[#6E7787] text-[11px] leading-relaxed mt-4">
          By continuing you agree to our{" "}
          <Link to="/terms" className="text-[#A0AABF] hover:text-white underline">Terms</Link>{" "}and{" "}
          <Link to="/privacy" className="text-[#A0AABF] hover:text-white underline">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}
